/**
 * SponsorshipAdmin.tsx  — v2
 *
 * Handles all three pathways for getting a sponsored seat to a child:
 *
 * PATH 1 — Hardship Applications (self-applied)
 *   Families/individuals who submitted a hardship application.
 *   Already in scholarship_applications with status='approved'.
 *   Admin assigns a sponsor seat or gift enrolment to them.
 *
 * PATH 2 — Org Outreach (admin-initiated)
 *   Sage reaches out to an orphanage, NGO, church, youth group.
 *   Admin creates the org record, adds children manually.
 *   Bulk assigns seats to the whole group at once.
 *
 * PATH 3 — Recurring Multi-Seat (sponsors)
 *   Corporate/individual sponsors paying monthly/yearly for N seats.
 *   Admin assigns each seat to a child from Path 1 or Path 2.
 *
 * All seat assignments flow into sponsor_seat_assignments.
 * gift_enrolments is used when the seat is a one-time gift code.
 */

import { useState, useEffect, useCallback } from 'react'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Heart, Building2, Globe, Church, Users, Search,
  RefreshCw, ChevronDown, ChevronUp, CheckCircle2,
  Clock, Tag, Plus, Mail, Loader2, X, Edit3,
  UserPlus, Package, AlertCircle, ExternalLink,
} from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Sponsorship {
  id: string
  created_at: string
  sponsor_id: string
  sponsor_type: string
  status: string
  quantity: number
  seats_used: number
  seats_allocated: number
  plan_key: string | null
  billing_interval: string
  organisation_name: string | null
  organisation_logo_url: string | null
  social_handle: string | null
  social_platform: string | null
  allow_social_tag: boolean
  allow_logo_use: boolean
  dedication_message: string | null
  anonymous: boolean
  is_pay_it_forward: boolean
  next_billing_date: string | null
  update_preference: string | null
  sponsor_email?: string
  sponsor_name?: string
  assignments?: Assignment[]
}

interface Assignment {
  id: string
  assigned_name: string | null
  assigned_email: string | null
  status: string
  plan_key: string | null
  activated_at: string | null
  expires_at: string | null
  scholarship_app_id: string | null
  org_child_id: string | null
}

interface PoolEntry {
  id: string
  type: 'recurring' | 'one-time'
  donor_name: string | null
  donor_email: string | null
  plan_key: string
  seats_contributed: number
  seats_remaining: number
  billing_interval: string | null
  organisation_name: string | null
  allow_social_tag: boolean | null
  allow_logo_use: boolean | null
  status: string | null
  reference_id?: string // For gift_code or sponsorship_id
}

interface HardshipApp {
  id: string
  applicant_name: string
  applicant_email: string
  application_type: string
  status: string | null
  country: string | null
  number_of_children: number | null
  hardship_type: string | null
}

interface OrgNomination {
  id: string
  org_name: string
  org_type: string
  org_country: string | null
  org_city: string | null
  org_contact_name: string | null
  org_contact_email: string | null
  seats_allocated: number
  seats_used: number
  plan_key: string | null
  sponsorship_id: string | null
  status: string
  children_count?: number
  pending_count?: number
}

interface OrgChild {
  id: string
  org_id: string
  child_name: string
  child_age: number | null
  guardian_name: string | null
  guardian_email: string | null
  status: string
  assignment_id: string | null
}

interface SeatRequest {
  id: string
  org_id: string
  org_name?: string
  org_contact_email?: string
  seats_requested: number
  reason: string | null
  status: string
  admin_note: string | null
  created_at: string | null
}

interface NfpOrganisation {
  id: string
  created_at: string | null
  name: string
  contact_name: string
  contact_email: string
  contact_phone: string | null
  website: string | null
  country: string
  charity_number: string | null
  organisation_type: string
  cause_area: string | null
  application_status: string | null
  application_message: string | null
  admin_notes: string | null
  facility_id: string | null
  max_seats?: number | null
  reviewed_by: string | null
  reviewed_at: string | null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function Badge({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${color}`}>
      {children}
    </span>
  )
}

const ORG_TYPE_ICONS: Record<string, React.ReactNode> = {
  orphanage:        <Heart size={14} className="text-rose-500" />,
  ngo:              <Globe size={14} className="text-blue-500" />,
  church:           <Church size={14} className="text-amber-500" />,
  youth_group:      <Users size={14} className="text-purple-500" />,
  school:           <Building2 size={14} className="text-slate-500" />,
  community_centre: <Building2 size={14} className="text-teal-500" />,
  other:            <Globe size={14} className="text-slate-400" />,
}

// ── Main Component ────────────────────────────────────────────────────────────

export function SponsorshipAdmin() {
  const supabase = createClientComponentClient()

  const [tab, setTab]                   = useState<'sponsors' | 'pool' | 'orgs' | 'requests' | 'applications' | 'assign'>('sponsors')
  const [sponsorships, setSponsorships] = useState<Sponsorship[]>([])
  const [pool, setPool]                 = useState<PoolEntry[]>([])
  const [hardshipApps, setHardshipApps] = useState<HardshipApp[]>([])
  const [orgs, setOrgs]                 = useState<OrgNomination[]>([])
  const [seatRequests, setSeatRequests]  = useState<SeatRequest[]>([])
  const [nfpApps, setNfpApps]           = useState<NfpOrganisation[]>([])
  const [loading, setLoading]           = useState(true)
  const [search, setSearch]             = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter]     = useState('all')
  const [expandedId, setExpandedId]     = useState<string | null>(null)

  // Assign form — works for all three paths
  const [assignForm, setAssignForm] = useState({
    source:           'hardship' as 'hardship' | 'org',
    sponsorship_id:   '',
    app_id:           '',          // hardship app id
    org_id:           '',          // org nomination id
    plan_key:         'edu_bundle',  // platform policy: every sponsored child gets the Full Bundle
    duration_months:  12,          // fallback only; effectiveDuration reads sponsorship notes first
  })
  const [assigning, setAssigning]         = useState(false)

  // Org management
  const [showOrgForm, setShowOrgForm]     = useState(false)
  const [selectedOrg, setSelectedOrg]     = useState<OrgNomination | null>(null)
  const [orgChildren, setOrgChildren]     = useState<OrgChild[]>([])
  const [showChildForm, setShowChildForm] = useState(false)
  const [childForm, setChildForm]         = useState({
    child_name: '', child_age: '', guardian_name: '', guardian_email: '', guardian_phone: '', notes: ''
  })
  const [orgForm, setOrgForm] = useState({
    org_name: '', org_type: 'orphanage', org_country: '', org_city: '',
    org_contact_name: '', org_contact_email: '', org_contact_phone: '',
    seats_allocated: 10, plan_key: 'edu_bundle', outreach_source: '', org_notes: ''
  })
  const [savingOrg, setSavingOrg]       = useState(false)
  const [savingChild, setSavingChild]   = useState(false)
  const [invitingNgoId, setInvitingNgoId] = useState<string | null>(null)

  // ── Load all data ────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true)
    try {
      // Sponsorships
      const { data: fss } = await supabase
        .from('facility_seat_sponsorships').select('*').order('created_at', { ascending: false })
      const enriched = await Promise.all((fss || []).map(async (s: any) => {
        const { data: u } = await supabase.from('users').select('email, name').eq('id', s.sponsor_id).maybeSingle()
        const { data: a } = await supabase.from('sponsor_seat_assignments').select('*').eq('sponsorship_id', s.id)
        return { ...s, sponsor_email: u?.email, sponsor_name: u?.name, assignments: a ?? [] }
      }))
      setSponsorships(enriched)

      // Pool
      const { data: poolData } = await supabase.from('pay_it_forward_pool').select('*').order('created_at', { ascending: false })
      // Single source of truth: pay_it_forward_pool. One-time pool gifts now
      // materialize as pool rows via the webhook — the gift_enrolments merge
      // is retired (it would double-count them).
      const combinedPool: PoolEntry[] = (poolData || []).map(p => ({
        ...p,
        type: p.billing_interval === 'one-time' ? ('one-time' as const) : ('recurring' as const),
      }))
      setPool(combinedPool)

      // Hardship apps — approved, not yet granted
      const { data: apps } = await supabase
        .from('scholarship_applications')
        .select('id,applicant_name,applicant_email,application_type,status,country,number_of_children,hardship_type')
        .eq('status', 'approved').is('granted_plan_key', null)
        .order('created_at', { ascending: false })
      setHardshipApps(apps ?? [])

      // Org nominations
      const { data: orgData } = await supabase
        .from('org_nominations').select('*').order('created_at', { ascending: false })
      // Enrich with child counts
      const enrichedOrgs = await Promise.all((orgData || []).map(async (o: any) => {
        const { count: total }   = await supabase.from('org_nomination_children').select('id', { count: 'exact', head: true }).eq('org_id', o.id)
        const { count: pending } = await supabase.from('org_nomination_children').select('id', { count: 'exact', head: true }).eq('org_id', o.id).eq('status', 'pending')
        return { ...o, children_count: total ?? 0, pending_count: pending ?? 0 }
      }))
      setOrgs(enrichedOrgs)

      // Seat Requests
      const { data: requests } = await supabase
        .from('org_seat_requests').select(`
          id, org_id, seats_requested, reason, status, admin_note, created_at,
          org_nominations(org_name, org_contact_email)
        `).order('created_at', { ascending: false })
      setSeatRequests((requests || []).map((r: any) => ({
        ...r, org_name: r.org_nominations?.org_name, org_contact_email: r.org_nominations?.org_contact_email
      })))

      // NFP Partner Applications
      const { data: appsData } = await supabase.from('nfp_organisations').select('*').order('created_at', { ascending: false })
      setNfpApps((appsData || []) as unknown as NfpOrganisation[])

    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [supabase])

  useEffect(() => { load() }, [load])

  // Load children for selected org
  useEffect(() => {
    if (!selectedOrg) return
    supabase.from('org_nomination_children')
      .select('*').eq('org_id', selectedOrg.id).order('created_at', { ascending: false })
      .then(({ data }) => setOrgChildren(data ?? []))
  }, [selectedOrg, supabase])

  // ── Assign seat ─────────────────────────────────────────────────────────────
  async function assignSeat() {
    if (!assignForm.sponsorship_id) { alert('Select a sponsorship or pool source'); return }
    if (assignForm.source === 'hardship' && !assignForm.app_id) { alert('Select a hardship applicant'); return }
    if (assignForm.source === 'org' && !assignForm.org_id) { alert('Select an organisation'); return }
    setAssigning(true)

    try {
      const { data, error } = await supabase.functions.invoke('assign-sponsor-seat', {
        body: {
          source: assignForm.source,
          sponsorship_id: assignForm.sponsorship_id,
          app_id: assignForm.app_id || undefined,
          org_id: assignForm.org_id || undefined,
          plan_key: assignForm.plan_key,
          duration_months: assignForm.duration_months,
        },
      });

      if (error || data?.error) {
        throw new Error(error?.message || data?.error);
      }

      await load();
      setAssignForm((f) => ({ ...f, app_id: '', org_id: '' }));
      alert(`✓ Seat successfully assigned and welcome email sent!`);
    } catch (e: any) {
      alert(e.message);
    } finally {
      setAssigning(false);
    }
  }

  // ── Bulk assign all pending org children ────────────────────────────────────
  async function bulkAssignOrg(orgId: string, sponsorshipId: string, planKey: string) {
    if (!confirm('Assign a seat to every pending child in this organisation?')) return;
    try {
      const { data, error } = await supabase.functions.invoke('assign-sponsor-seat', {
        body: {
          source: 'bulk_org',
          sponsorship_id: sponsorshipId,
          org_id: orgId,
          plan_key: planKey,
          duration_months: 12,
        },
      });

      if (error || data?.error) {
        throw new Error(error?.message || data?.error);
      }

      await load();
      if (selectedOrg?.id === orgId) {
        const { data: refreshedChildren } = await supabase
          .from('org_nomination_children')
          .select('*')
          .eq('org_id', orgId)
          .order('created_at', { ascending: false });
        setOrgChildren(refreshedChildren ?? []);
      }
      alert(`✓ ${data.assigned_count || 'All pending'} seats assigned and welcome emails dispatched!`);
    } catch (e: any) {
      alert(e.message);
    }
  }

  // ── NGO invite (magic link) ──────────────────────────────────────────────────
  async function sendNgoInvite(orgId: string, email: string, orgName: string) {
    if (!email) { alert('No contact email set for this organisation.'); return }
    if (!confirm(`Send magic link invitation to ${email}?`)) return
    setInvitingNgoId(orgId)
    try {
      const { error } = await supabase.functions.invoke('invite-ngo', {
        body: { orgId, email, orgName }
      })
      if (error) throw error
      alert(`✓ Invitation sent to ${email}`)
    } catch (e: any) {
      console.error('NGO invite failed:', e)
      alert(`Failed to send invite: ${e.message}`)
    } finally {
      setInvitingNgoId(null)
    }
  }

  // ── Save new org ─────────────────────────────────────────────────────────────
  async function saveOrg() {
    if (!orgForm.org_name.trim()) { alert('Organisation name required'); return }
    setSavingOrg(true)
    try {
      const orgEmail = orgForm.org_contact_email ? orgForm.org_contact_email.trim().toLowerCase() : null
      if (orgEmail) {
        const { data: dupe } = await supabase.from('org_nominations')
          .select('id').eq('org_contact_email', orgEmail).maybeSingle()
        if (dupe) { alert('An organisation with this contact email already exists.'); return }
      }
      await supabase.from('org_nominations').insert({
        org_name:          orgForm.org_name.trim(),
        org_type:          orgForm.org_type,
        org_country:       orgForm.org_country || null,
        org_city:          orgForm.org_city || null,
        org_contact_name:  orgForm.org_contact_name || null,
        org_contact_email: orgEmail,
        seats_allocated:   Number(orgForm.seats_allocated),
        plan_key:          orgForm.plan_key,
        outreach_source:   orgForm.outreach_source || null,
        org_notes:         orgForm.org_notes || null,
        status:            'active',
      })
      setShowOrgForm(false)
      setOrgForm({ org_name:'', org_type:'orphanage', org_country:'', org_city:'', org_contact_name:'', org_contact_email:'', org_contact_phone:'', seats_allocated:10, plan_key:'edu_bundle', outreach_source:'', org_notes:'' })
      await load()
    } catch (e: any) { alert(e.message) }
    finally { setSavingOrg(false) }
  }

  // ── Save new child to org ────────────────────────────────────────────────────
  async function saveChild() {
    if (!selectedOrg || !childForm.child_name.trim()) { alert('Child name required'); return }
    setSavingChild(true)
    try {
      await supabase.from('org_nomination_children').insert({
        org_id:          selectedOrg.id,
        child_name:      childForm.child_name.trim(),
        child_age:       childForm.child_age ? Number(childForm.child_age) : null,
        guardian_name:   childForm.guardian_name || null,
        guardian_email:  childForm.guardian_email ? childForm.guardian_email.trim().toLowerCase() : null,
        guardian_phone:  childForm.guardian_phone || null,
        notes:           childForm.notes || null,
        status:          'pending',
      })
      setShowChildForm(false)
      setChildForm({ child_name:'', child_age:'', guardian_name:'', guardian_email:'', guardian_phone:'', notes:'' })
      const { data } = await supabase.from('org_nomination_children').select('*').eq('org_id', selectedOrg.id).order('created_at', { ascending: false })
      setOrgChildren(data ?? [])
      await load()
    } catch (e: any) { alert(e.message) }
    finally { setSavingChild(false) }
  }

  // ── Derived stats ────────────────────────────────────────────────────────────
  // Pool seats: all active entries with seats remaining (recurring AND one-time;
  // one-time rows decrement truthfully on assignment, receipted in the function)
  const totalRecurringPoolSeats = pool.filter(p => p.status === 'active' && p.seats_remaining > 0).reduce((a, p) => a + p.seats_remaining, 0)
  const totalActiveSponsors    = sponsorships.filter(s => s.status === 'active').length
  const totalActiveSponsorSeats= sponsorships.filter(s => s.status === 'active').reduce((a, s) => a + s.quantity, 0)
  const totalOrgChildren       = orgs.reduce((a, o) => a + (o.children_count ?? 0), 0)
  const totalPendingOrgSeats   = orgs.reduce((a, o) => a + (o.pending_count ?? 0), 0)
  const socialTagSponsors      = sponsorships.filter(s => s.allow_social_tag)
  const logoSponsors           = sponsorships.filter(s => s.allow_logo_use)

  const filteredSponsors = sponsorships.filter(s => {
    const ms = !search || [s.sponsor_name,s.sponsor_email,s.organisation_name].some(v => v?.toLowerCase().includes(search.toLowerCase()))
    const mst = statusFilter === 'all' || s.status === statusFilter
    const mt  = typeFilter   === 'all' || s.sponsor_type === typeFilter
    return ms && mst && mt
  })

  const totalPoolSeats = totalRecurringPoolSeats // alias used in tab labels

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-8 space-y-8">

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h2 className="text-3xl font-black text-slate-900 flex items-center gap-3">
            <Heart className="w-8 h-8 text-rose-500" /> Sponsorship Management
          </h2>
          <p className="text-slate-500 font-medium mt-1">Sponsors · Pool · Org outreach · Seat assignment</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Active Sponsors',    val: totalActiveSponsors,     color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100' },
          { label: 'Total Funded Seats', val: totalActiveSponsorSeats, color: 'text-blue-600',    bg: 'bg-blue-50 border-blue-100' },
          { label: 'Pool Seats',         val: totalPoolSeats,           color: 'text-amber-600',   bg: 'bg-amber-50 border-amber-100' },
          { label: 'Hardship Awaiting',  val: hardshipApps.length,     color: 'text-rose-600',    bg: 'bg-rose-50 border-rose-100' },
          { label: 'Org Children Pending', val: totalPendingOrgSeats,  color: 'text-violet-600',  bg: 'bg-violet-50 border-violet-100' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} border rounded-2xl p-4`}>
            <p className={`text-2xl font-black ${s.color}`}>{s.val}</p>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Marketing opt-in alert */}
      {(socialTagSponsors.length > 0 || logoSponsors.length > 0) && (
        <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4">
          <p className="text-xs font-black text-violet-700 uppercase tracking-widest mb-2">📸 Marketing Opt-Ins</p>
          {socialTagSponsors.length > 0 && (
            <p className="text-sm text-violet-800 font-medium">
              <strong>{socialTagSponsors.length}</strong> sponsor{socialTagSponsors.length > 1 ? 's' : ''} opted in to social tagging:
              {' '}{socialTagSponsors.map(s => s.social_handle || s.organisation_name || s.sponsor_name).filter(Boolean).join(', ')}
            </p>
          )}
          {logoSponsors.length > 0 && (
            <p className="text-sm text-violet-800 font-medium mt-1">
              <strong>{logoSponsors.length}</strong> organisation{logoSponsors.length > 1 ? 's' : ''} approved logo use in impact photos.
            </p>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 bg-slate-100 p-1 rounded-2xl w-fit flex-wrap">
        {[
          { id: 'sponsors',     label: 'Sponsors' },
          { id: 'pool',         label: `Pool (${totalPoolSeats})` },
          { id: 'orgs',         label: `Org Outreach (${orgs.length})` },
          { id: 'requests',     label: `Requests (${seatRequests.filter(r => r.status === 'pending').length})` },
          { id: 'applications', label: `Applications` },
          { id: 'assign',       label: `Assign Seats (${hardshipApps.length + totalPendingOrgSeats})` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id as any)}
            className={`px-4 py-2.5 rounded-xl text-sm font-black transition-all ${tab === t.id ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB: SPONSORS ─────────────────────────────────────────────────────── */}
      {tab === 'sponsors' && (
        <>
          <div className="flex flex-wrap gap-3 bg-white/60 p-4 rounded-2xl border border-slate-200">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input placeholder="Search by name, org, email…" value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-medium focus:outline-none" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 focus:outline-none">
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              className="px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-600 focus:outline-none">
              <option value="all">All Types</option>
              <option value="individual">Individual</option>
              <option value="business">Business</option>
              <option value="ngo">NGO</option>
              <option value="church">Church</option>
            </select>
          </div>

          <div className="space-y-3">
            {filteredSponsors.map(s => {
              const isOpen = expandedId === s.id
              return (
                <div key={s.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className="p-5 cursor-pointer" onClick={() => setExpandedId(isOpen ? null : s.id)}>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.status === 'active' ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                          {s.sponsor_type === 'business' ? <Building2 size={16} className="text-slate-600" />
                            : s.sponsor_type === 'ngo' ? <Globe size={16} className="text-slate-600" />
                            : s.sponsor_type === 'church' ? <Church size={16} className="text-slate-600" />
                            : <Heart size={16} className="text-rose-500" />}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-bold text-slate-900 text-sm">{s.organisation_name || s.sponsor_name || s.sponsor_email}</p>
                            {s.is_pay_it_forward && <Badge color="bg-amber-100 text-amber-700 border-amber-200">Pool</Badge>}
                            {s.allow_social_tag   && <Badge color="bg-violet-100 text-violet-700 border-violet-200"><Tag size={9} /> Tag</Badge>}
                          </div>
                          <p className="text-xs text-slate-400 font-medium">{s.sponsor_email} · {s.sponsor_type} · {s.billing_interval}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right hidden sm:block">
                          <p className="text-lg font-black text-slate-900">{s.quantity} seats</p>
                          <p className="text-xs text-slate-400">{s.seats_used} used · {s.quantity - s.seats_used} available</p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${s.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          {s.status}
                        </span>
                        {isOpen ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                      </div>
                    </div>
                  </div>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        className="border-t border-slate-100 overflow-hidden">
                        <div className="p-5 space-y-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                              { label: 'Plan',        val: s.plan_key ?? '—' },
                              { label: 'Billing',     val: s.billing_interval },
                              { label: 'Update Pref', val: s.update_preference ?? 'none' },
                              { label: 'Next Billing', val: s.next_billing_date ? new Date(s.next_billing_date).toLocaleDateString('en-GB', { day:'numeric',month:'short',year:'numeric' }) : '—' },
                              { label: 'Social',      val: s.social_handle ? `${s.social_platform}: ${s.social_handle}` : '—' },
                              { label: 'Dedication',  val: s.dedication_message ? `"${s.dedication_message.substring(0,40)}…"` : '—' },
                            ].map(r => (
                              <div key={r.label} className="bg-slate-50 rounded-xl p-3">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{r.label}</p>
                                <p className="font-bold text-slate-700 text-xs truncate">{r.val}</p>
                              </div>
                            ))}
                          </div>
                          {(s.assignments ?? []).length > 0 && (
                            <div>
                              <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Assigned Children ({s.assignments?.length})</p>
                              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                {(s.assignments ?? []).map((a: Assignment) => (
                                  <div key={a.id} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2">
                                    <p className="text-sm font-medium text-slate-700">{a.assigned_name ?? a.assigned_email ?? 'Pending'}</p>
                                    <span className={`text-xs font-bold ${a.status === 'active' ? 'text-emerald-600' : 'text-amber-600'}`}>{a.status}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
            {filteredSponsors.length === 0 && !loading && (
              <div className="text-center py-16 text-slate-400">
                <Heart className="mx-auto mb-3 opacity-30" size={36} />
                <p className="font-bold">No sponsorships match your filters</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── TAB: POOL ──────────────────────────────────────────────────────────── */}
      {tab === 'pool' && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <p className="font-black text-amber-800 text-sm mb-1">🌱 Pay It Forward Pool: {totalPoolSeats} seats available</p>
            <p className="text-amber-700 text-sm font-medium">Admin assigns these seats to approved hardship applicants or org-outreach children from the Assign tab.</p>
          </div>
          {pool.map(p => (
            <div key={p.id} className={`bg-white border rounded-2xl p-5 ${p.type === 'one-time' ? 'border-violet-100 bg-violet-50/30' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-bold text-slate-900">{p.organisation_name || p.donor_name || 'Anonymous Donor'}</p>
                    <Badge color={p.type === 'one-time' ? 'bg-violet-100 text-violet-700 border-violet-200' : 'bg-blue-100 text-blue-700 border-blue-200'}>
                      {p.type === 'one-time' ? 'One-time' : 'Recurring'}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400 font-medium">
                    {p.donor_email} · {p.plan_key}
                    {p.type === 'one-time' && p.reference_id ? ` · Ref: ${p.reference_id}` : ` · ${p.billing_interval}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-black text-slate-900">{p.seats_remaining}<span className="text-slate-400 text-sm font-bold">/{p.seats_contributed}</span></p>
                  <p className="text-xs text-slate-400 font-medium">seats remaining</p>
                </div>
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                {p.allow_social_tag && <Badge color="bg-violet-100 text-violet-700 border-violet-200"><Tag size={9}/> Tag OK</Badge>}
                {p.allow_logo_use   && <Badge color="bg-blue-100 text-blue-700 border-blue-200">Logo OK</Badge>}
                <Badge color={p.status === 'active' || p.seats_remaining > 0 ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}>
                  {p.seats_remaining > 0 ? 'available' : p.status}
                </Badge>
              </div>
            </div>
          ))}
          {pool.length === 0 && <div className="text-center py-16 text-slate-400"><p className="font-bold">No pool contributions yet</p></div>}
        </div>
      )}

      {/* ── TAB: ORG OUTREACH ─────────────────────────────────────────────────── */}
      {tab === 'orgs' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-black text-slate-900 text-lg">Organisation Outreach</p>
              <p className="text-sm text-slate-500 font-medium">Orphanages, NGOs, churches, youth groups Sage has engaged directly.</p>
            </div>
            <button onClick={() => setShowOrgForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-xl transition-all">
              <Plus size={15} /> Add Organisation
            </button>
          </div>

          {/* New org form */}
          <AnimatePresence>
            {showOrgForm && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                className="bg-white border border-emerald-200 rounded-2xl p-6 space-y-4 overflow-hidden">
                <div className="flex items-center justify-between">
                  <p className="font-black text-slate-900">New Organisation</p>
                  <button onClick={() => setShowOrgForm(false)}><X size={16} className="text-slate-400" /></button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Organisation Name *', key: 'org_name', type: 'text', placeholder: 'St. Joseph\'s Orphanage' },
                    { label: 'Contact Email',       key: 'org_contact_email', type: 'email', placeholder: 'contact@org.org' },
                    { label: 'Country',             key: 'org_country', type: 'text', placeholder: 'Kenya' },
                    { label: 'City',                key: 'org_city', type: 'text', placeholder: 'Nairobi' },
                    { label: 'Contact Name',        key: 'org_contact_name', type: 'text', placeholder: 'Fr. Michael' },
                    { label: 'Seats Allocated',     key: 'seats_allocated', type: 'number', placeholder: '10' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-xs font-bold text-slate-500 mb-1">{f.label}</label>
                      <input type={f.type} value={(orgForm as any)[f.key]} placeholder={f.placeholder}
                        onChange={e => setOrgForm(o => ({ ...o, [f.key]: e.target.value }))}
                        className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium focus:outline-none" />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1">Org Type</label>
                    <select value={orgForm.org_type} onChange={e => setOrgForm(o => ({ ...o, org_type: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-semibold focus:outline-none">
                      {['orphanage','ngo','church','youth_group','school','community_centre','other'].map(t => (
                        <option key={t} value={t}>{t.replace('_',' ')}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1">Default Plan</label>
                    <select value={orgForm.plan_key} onChange={e => setOrgForm(o => ({ ...o, plan_key: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-semibold focus:outline-none">
                      <option value="homeschool_single">Standard Curriculum</option>
                      <option value="edu_elite">Elite Academy</option>
                      <option value="edu_bundle">Full Bundle</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Internal Notes</label>
                  <textarea value={orgForm.org_notes} onChange={e => setOrgForm(o => ({ ...o, org_notes: e.target.value }))} rows={2}
                    placeholder="How we found them, context, special circumstances…"
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium focus:outline-none resize-none" />
                </div>
                <button onClick={saveOrg} disabled={savingOrg}
                  className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold rounded-xl flex items-center justify-center gap-2">
                  {savingOrg ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : <><CheckCircle2 size={15} /> Save Organisation</>}
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Org list */}
          <div className="space-y-3">
            {orgs.map(o => (
              <div key={o.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="p-5 cursor-pointer" onClick={() => setSelectedOrg(selectedOrg?.id === o.id ? null : o)}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                        {ORG_TYPE_ICONS[o.org_type] ?? <Globe size={16} />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-slate-900">{o.org_name}</p>
                          <Badge color="bg-slate-100 text-slate-600 border-slate-200">{o.org_type.replace('_',' ')}</Badge>
                          <Badge color={o.status === 'active' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}>{o.status}</Badge>
                        </div>
                        <p className="text-xs text-slate-400 font-medium">
                          {[o.org_city, o.org_country].filter(Boolean).join(', ')}
                          {o.org_contact_email ? ` · ${o.org_contact_email}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right mr-3">
                        <p className="font-black text-slate-900">{o.children_count ?? 0} children</p>
                        <p className="text-xs text-slate-400">{o.pending_count ?? 0} pending assignment</p>
                      </div>
                      {o.org_contact_email && (
                        <button
                          onClick={(e) => { e.stopPropagation(); sendNgoInvite(o.id, o.org_contact_email!, o.org_name) }}
                          disabled={invitingNgoId === o.id}
                          className="p-2 rounded-xl bg-slate-50 border border-slate-200 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 hover:border-emerald-200 transition-all flex items-center justify-center"
                          title="Invite NGO contact via Magic Link"
                        >
                          {invitingNgoId === o.id ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded: children list + add child + bulk assign */}
                <AnimatePresence>
                  {selectedOrg?.id === o.id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="border-t border-slate-100 overflow-hidden">
                      <div className="p-5 space-y-4">

                        {/* Bulk assign CTA */}
                        {(o.pending_count ?? 0) > 0 && (
                          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-center justify-between">
                            <div>
                              <p className="font-bold text-emerald-800 text-sm">{o.pending_count} children awaiting seat assignment</p>
                              <p className="text-xs text-emerald-600 font-medium mt-0.5">Select a sponsorship below, then bulk assign all at once.</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <select
                                onChange={e => bulkAssignOrg(o.id, e.target.value, o.plan_key || 'edu_bundle')}
                                className="px-3 py-2 rounded-xl border border-emerald-200 bg-white text-sm font-semibold text-slate-700 focus:outline-none"
                                defaultValue="">
                                <option value="" disabled>Choose sponsorship…</option>
                                {sponsorships.filter(s => s.status === 'active' && s.quantity > s.seats_used).map(s => (
                                  <option key={s.id} value={s.id}>
                                    {s.is_pay_it_forward ? '[Pool]' : '[Named]'} {s.organisation_name || s.sponsor_name || s.sponsor_email} ({s.quantity - s.seats_used} free)
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}

                        {/* Children table */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Children</p>
                            <button onClick={() => setShowChildForm(true)}
                              className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 hover:text-emerald-700 px-3 py-1.5 rounded-lg hover:bg-emerald-50 transition-all">
                              <UserPlus size={12} /> Add Child
                            </button>
                          </div>

                          {/* Add child form */}
                          <AnimatePresence>
                            {showChildForm && (
                              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                                className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3 overflow-hidden">
                                <div className="grid grid-cols-2 gap-3">
                                  {[
                                    { label: 'Child Name *',      key: 'child_name',      placeholder: 'Emmanuel' },
                                    { label: 'Age',               key: 'child_age',        placeholder: '12' },
                                    { label: 'Guardian Name',     key: 'guardian_name',    placeholder: 'Sister Agnes' },
                                    { label: 'Guardian Email',    key: 'guardian_email',   placeholder: 'guardian@org.org' },
                                  ].map(f => (
                                    <div key={f.key}>
                                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{f.label}</label>
                                      <input value={(childForm as any)[f.key]} placeholder={f.placeholder}
                                        onChange={e => setChildForm(c => ({ ...c, [f.key]: e.target.value }))}
                                        className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none" />
                                    </div>
                                  ))}
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={() => setShowChildForm(false)}
                                    className="px-4 py-2 text-sm font-bold text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-100">Cancel</button>
                                  <button onClick={saveChild} disabled={savingChild}
                                    className="flex-1 py-2 bg-emerald-600 text-white text-sm font-bold rounded-lg flex items-center justify-center gap-2">
                                    {savingChild ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                                    Save Child
                                  </button>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {orgChildren.filter(c => c.org_id === o.id).map(child => (
                            <div key={child.id} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                              <div>
                                <p className="font-bold text-slate-800 text-sm">{child.child_name}
                                  {child.child_age ? <span className="text-slate-400 font-medium"> · age {child.child_age}</span> : null}
                                </p>
                                <p className="text-xs text-slate-400">{child.guardian_email || 'No guardian email'}</p>
                              </div>
                              <span className={`text-xs font-bold px-2 py-1 rounded-lg ${
                                child.status === 'active'   ? 'bg-emerald-100 text-emerald-700' :
                                child.status === 'assigned' ? 'bg-blue-100 text-blue-700' :
                                'bg-amber-100 text-amber-700'}`}>
                                {child.status}
                              </span>
                            </div>
                          ))}

                          {orgChildren.filter(c => c.org_id === o.id).length === 0 && (
                            <p className="text-sm text-slate-400 font-medium italic py-2">No children added yet: click "Add Child" above.</p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
            {orgs.length === 0 && !loading && (
              <div className="text-center py-16 text-slate-400">
                <Globe className="mx-auto mb-3 opacity-30" size={36} />
                <p className="font-bold">No organisations added yet</p>
                <p className="text-sm mt-1">Click "Add Organisation" to register an orphanage, NGO, church or youth group.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB: ASSIGN SEATS ─────────────────────────────────────────────────── */}
      {tab === 'assign' && (
        <div className="space-y-6">

          {/* Source selector */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
            <h3 className="font-black text-slate-900 text-lg">Assign a Seat</h3>

            {/* Who is this for */}
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Who is this for?</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { val: 'hardship', icon: <Heart size={16} />, label: 'Hardship Applicant', desc: `${hardshipApps.length} approved, awaiting seat` },
                  { val: 'org',      icon: <Globe size={16} />, label: 'Org Outreach Child',  desc: `${totalPendingOrgSeats} pending across ${orgs.filter(o => (o.pending_count??0)>0).length} orgs` },
                ].map(opt => (
                  <button key={opt.val} onClick={() => setAssignForm(f => ({ ...f, source: opt.val as any }))}
                    className={`flex items-start gap-3 p-4 rounded-2xl border-2 text-left transition-all ${assignForm.source === opt.val ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-emerald-300'}`}>
                    <span className={`mt-0.5 ${assignForm.source === opt.val ? 'text-emerald-600' : 'text-slate-400'}`}>{opt.icon}</span>
                    <div>
                      <p className={`font-bold text-sm ${assignForm.source === opt.val ? 'text-emerald-700' : 'text-slate-700'}`}>{opt.label}</p>
                      <p className="text-xs text-slate-400 font-medium mt-0.5">{opt.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Hardship applicant selector */}
            {assignForm.source === 'hardship' && (
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5">Approved Applicant</label>
                <select value={assignForm.app_id} onChange={e => setAssignForm(f => ({ ...f, app_id: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium focus:outline-none">
                  <option value="">Choose applicant…</option>
                  {hardshipApps.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.applicant_name} ({a.applicant_email}) · {a.application_type}{a.country ? ` · ${a.country}` : ''}
                    </option>
                  ))}
                </select>
                {hardshipApps.length === 0 && (
                  <p className="text-xs text-emerald-600 font-bold mt-1">✓ All approved applicants have been assigned.</p>
                )}
              </div>
            )}

            {/* Org selector */}
            {assignForm.source === 'org' && (
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5">Organisation (assigns all pending children, up to free seats)</label>
                <select value={assignForm.org_id} onChange={e => setAssignForm(f => ({ ...f, org_id: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium focus:outline-none">
                  <option value="">Choose organisation…</option>
                  {orgs.filter(o => (o.pending_count ?? 0) > 0).map(o => (
                    <option key={o.id} value={o.id}>
                      {o.org_name}: {o.pending_count} pending ({o.org_type}, {o.org_country || '?'})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 font-medium mt-1">Assigns every pending child in that organisation, capped to the sponsorship's free seats.</p>
              </div>
            )}

            {/* Sponsorship source */}
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5">Sponsorship / Pool Source</label>
              <select value={assignForm.sponsorship_id} onChange={e => setAssignForm(f => ({ ...f, sponsorship_id: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium focus:outline-none">
                <option value="">Choose a sponsorship…</option>
                <optgroup label="Recurring Sponsorships (Pool)">
                  {sponsorships.filter(s => s.status === 'active' && s.is_pay_it_forward && s.quantity > s.seats_used).map(s => (
                    <option key={s.id} value={s.id}>
                      {s.organisation_name || s.sponsor_name || s.sponsor_email}: {s.quantity - s.seats_used} seats free
                    </option>
                  ))}
                </optgroup>
                {/* One-time gift pool retired — pool gifts materialize as 1-seat
                    sponsorships (webhook) and appear above with their "Gift: …" label. */}
                <optgroup label="Direct/Named Sponsorships">
                  {sponsorships.filter(s => s.status === 'active' && !s.is_pay_it_forward && s.quantity > s.seats_used).map(s => (
                    <option key={s.id} value={s.id}>
                      {s.organisation_name || s.sponsor_name || s.sponsor_email}: {s.quantity - s.seats_used} seats free
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>

            {/* Plan */}
            <div>
              <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1.5">Plan to Grant</label>
              <select value={assignForm.plan_key} onChange={e => setAssignForm(f => ({ ...f, plan_key: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium focus:outline-none">
                <option value="edu_bundle">Full Bundle, Standard + Elite + Bible Journey</option>
                <option value="homeschool_single">Standard Curriculum (84 subjects)</option>
                <option value="edu_elite">Elite Academy (30 courses)</option>
              </select>
            </div>

            <button onClick={assignSeat}
              disabled={assigning || !assignForm.sponsorship_id || (assignForm.source === 'hardship' ? !assignForm.app_id : !assignForm.org_id)}
              className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2">
              {assigning
                ? <><Loader2 size={16} className="animate-spin" /> Assigning…</>
                : <><CheckCircle2 size={16} /> Assign Seat & Grant Access</>}
            </button>
          </div>

          {/* Pending list — hardship */}
          {hardshipApps.length > 0 && (
            <div>
              <p className="font-black text-slate-900 mb-3">Approved Hardship Applicants, Awaiting Seat ({hardshipApps.length})</p>
              <div className="space-y-2">
                {hardshipApps.map(a => (
                  <div key={a.id} className="bg-white border border-slate-200 rounded-2xl px-5 py-4 flex items-center justify-between">
                    <div>
                      <p className="font-bold text-slate-800 text-sm">{a.applicant_name}</p>
                      <p className="text-xs text-slate-400 font-medium">{a.applicant_email} · {a.application_type}{a.country ? ` · ${a.country}` : ''}</p>
                    </div>
                    <button onClick={() => { setAssignForm(f => ({ ...f, source: 'hardship', app_id: a.id })); setTab('assign') }}
                      className="text-xs font-bold text-emerald-600 hover:text-emerald-700 px-3 py-1.5 rounded-lg hover:bg-emerald-50">
                      Select →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending list — orgs */}
          {orgs.filter(o => (o.pending_count ?? 0) > 0).length > 0 && (
            <div>
              <p className="font-black text-slate-900 mb-3">Org Outreach:  Children Awaiting Seats</p>
              <div className="space-y-2">
                {orgs.filter(o => (o.pending_count ?? 0) > 0).map(o => (
                  <div key={o.id} className="bg-white border border-slate-200 rounded-2xl px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-base">{o.org_type === 'orphanage' ? '🏠' : o.org_type === 'church' ? '⛪' : '🌍'}</span>
                      <div>
                        <p className="font-bold text-slate-800 text-sm">{o.org_name}</p>
                        <p className="text-xs text-slate-400">{[o.org_city, o.org_country].filter(Boolean).join(', ')} · {o.pending_count} children pending</p>
                      </div>
                    </div>
                    <button onClick={() => setAssignForm(f => ({ ...f, source: 'org', org_id: o.id }))}
                      className="text-xs font-bold text-emerald-600 hover:text-emerald-700 px-3 py-1.5 rounded-lg hover:bg-emerald-50">
                      Select →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB: REQUESTS ─────────────────────────────────────────────────────── */}
      {tab === 'requests' && (
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-2xl border border-slate-200">
            <h2 className="text-xl font-black text-slate-900 mb-2">NGO Seat Requests</h2>
            <p className="text-sm text-slate-500 mb-6">Review requests from NGOs for additional seats. Approving a request updates their allocated seat count.</p>
            {seatRequests.length === 0 ? (
              <p className="text-center text-slate-400 font-medium py-8 italic">No seat requests found.</p>
            ) : (
              <div className="space-y-4">
                {seatRequests.map(r => (
                  <div key={r.id} className="bg-slate-50 rounded-xl p-5 border border-slate-200 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-slate-900">{r.org_name}</h3>
                        <Badge color={r.status === 'pending' ? 'bg-amber-100 text-amber-700 border-amber-200' : r.status === 'approved' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-100 text-red-700 border-red-200'}>
                          {r.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mb-2">Requested <strong className="text-slate-900">{r.seats_requested} seats</strong> on {r.created_at ? new Date(r.created_at).toLocaleDateString() : 'Pending'}</p>
                      {r.reason && (
                        <p className="text-sm text-slate-600 bg-white p-3 rounded-lg border border-slate-100 mb-2">"{r.reason}"</p>
                      )}
                      {r.admin_note && (
                        <p className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md font-medium inline-flex items-center gap-1">
                          <CheckCircle2 size={12} /> Note: {r.admin_note}
                        </p>
                      )}
                    </div>
                    {r.status === 'pending' && (
                      <div className="flex flex-col gap-2 min-w-[240px]">
                        <button onClick={async () => {
                          const conf = confirm(`Approve ${r.seats_requested} seats for ${r.org_name}?`)
                          if (!conf) return
                          const note = prompt('Add optional admin note to the NGO:')
                          try {
                            setLoading(true)
                            await supabase.from('org_seat_requests').update({
                              status: 'approved', admin_note: note || null, reviewed_by: (await supabase.auth.getUser()).data.user?.id, reviewed_at: new Date().toISOString()
                            }).eq('id', r.id)
                            const { data: org } = await supabase.from('org_nominations').select('seats_allocated').eq('id', r.org_id).single()
                            if (org) {
                              await supabase.from('org_nominations').update({ seats_allocated: org.seats_allocated + r.seats_requested }).eq('id', r.org_id)
                            }
                            await load()
                          } catch (e: any) { alert(e.message) } finally { setLoading(false) }
                        }} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold transition-all w-full">
                          Approve Request
                        </button>
                        <button onClick={async () => {
                          const note = prompt('Reason for declining this request (will be shown to NGO):')
                          if (note === null) return
                          try {
                            setLoading(true)
                            await supabase.from('org_seat_requests').update({
                              status: 'declined', admin_note: note || null, reviewed_by: (await supabase.auth.getUser()).data.user?.id, reviewed_at: new Date().toISOString()
                            }).eq('id', r.id)
                            await load()
                          } catch (e: any) { alert(e.message) } finally { setLoading(false) }
                        }} className="px-4 py-2 bg-slate-100 hover:bg-red-50 hover:text-red-600 text-slate-600 rounded-xl text-sm font-bold transition-all w-full border border-slate-200 hover:border-red-200">
                          Decline Request
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB: PARTNER APPLICATIONS ─────────────────────────────────────────── */}
      {tab === 'applications' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="p-6 border-b border-slate-100 bg-slate-50/50">
              <h3 className="text-xl font-black text-slate-900 leading-tight">Partner Applications</h3>
              <p className="text-sm text-slate-500 font-medium">Applications arrive auto-approved and provisioned with zero seats, this is your visibility surface. Any pending item (rare) can still be approved manually.</p>
            </div>
            <div className="divide-y divide-slate-100">
              {nfpApps.map(app => (
                <div key={app.id} className="p-6 transition-colors hover:bg-slate-50/30">
                  <div className="flex flex-col lg:flex-row gap-6 lg:items-start justify-between">
                    <div className="space-y-4 flex-1">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600 flex-shrink-0">
                          <Building2 size={24} />
                        </div>
                        <div>
                          <h4 className="font-black text-slate-900 text-lg leading-tight">{app.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge color="bg-slate-100 text-slate-600 border-slate-200">{app.organisation_type}</Badge>
                            <span className="text-xs text-slate-400 font-bold uppercase tracking-tight">{app.country}</span>
                            <Badge color={app.application_status === 'approved' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : app.application_status === 'pending' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-red-100 text-red-700 border-red-200'}>
                              {app.application_status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Lead Contact</p>
                          <p className="text-sm font-bold text-slate-700 flex items-center gap-2"><Users size={14} className="text-slate-400" /> {app.contact_name}</p>
                          <p className="text-sm font-medium text-slate-500 flex items-center gap-2"><Mail size={14} className="text-slate-400" /> {app.contact_email}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Organisation Meta</p>
                          <p className="text-sm font-medium text-slate-500 flex items-center gap-2"><Globe size={14} className="text-slate-400" /> {app.website || 'No website provided'}</p>
                          {app.charity_number && (
                            <p className="text-sm font-medium text-slate-500 flex items-center gap-2"><Tag size={14} className="text-slate-400" /> {app.charity_number}</p>
                          )}
                        </div>
                      </div>
                      <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-3">
                        <div>
                          <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">Impact Goal</p>
                          <p className="text-sm text-slate-700 font-bold">{app.cause_area}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Application Message</p>
                          <p className="text-sm text-slate-600 leading-relaxed italic">"{app.application_message}"</p>
                        </div>
                      </div>
                    </div>
                    {app.application_status === 'pending' && (
                      <div className="flex flex-col gap-2 min-w-[200px]">
                        <button
                          onClick={async () => {
                            if (!confirm(`Approve ${app.name} as a partner? This will provision their organisation dashboard and dispatch an invite link.`)) return
                            try {
                              setLoading(true)
                              const { data: { user } } = await supabase.auth.getUser()

                              // 1. Provision org_nominations row (or find existing)
                              let orgId: string | null = null
                              const { data: existingOrg } = await supabase
                                .from('org_nominations')
                                .select('id')
                                .or(`org_contact_email.eq.${app.contact_email},org_name.eq.${app.name}`)
                                .maybeSingle()

                              if (existingOrg) {
                                orgId = existingOrg.id
                              } else {
                                const { data: newOrg, error: orgErr } = await supabase
                                  .from('org_nominations')
                                  .insert({
                                    org_name:          app.name,
                                    org_type:          app.organisation_type || 'charity',
                                    org_country:       app.country || null,
                                    org_contact_name:  app.contact_name,
                                    org_contact_email: app.contact_email,
                                    org_contact_phone: app.contact_phone || null,
                                    seats_allocated:   app.max_seats || 0,
                                    plan_key:          'edu_bundle',
                                    outreach_source:   'nfp_application',
                                    org_notes:         app.application_message || null,
                                    status:            'active',
                                  })
                                  .select()
                                  .single()

                                if (orgErr) throw orgErr
                                orgId = newOrg.id
                              }

                              // 2. Mark nfp application approved
                              await supabase.from('nfp_organisations').update({
                                application_status: 'approved',
                                reviewed_by: user?.id,
                                reviewed_at: new Date().toISOString()
                              }).eq('id', app.id)

                              // 3. Dispatch invite and link contact_user_id server-side
                              await supabase.functions.invoke('invite-ngo', {
                                body: { orgId, email: app.contact_email, orgName: app.name }
                              })

                              alert(`✓ Success! ${app.name} approved, provisioned in Org Outreach, and invite link sent to ${app.contact_email}.`)
                              await load()
                            } catch (e: any) {
                              alert(`Approval failed: ${e.message}`)
                            } finally { setLoading(false) }
                          }}
                          className="w-full py-3 bg-emerald-600 text-white font-black rounded-xl hover:bg-emerald-700 shadow-lg shadow-emerald-100 transition-all flex items-center justify-center gap-2"
                        >
                          <CheckCircle2 size={16} /> Approve & Provision
                        </button>
                        <button
                          onClick={async () => {
                            const reason = prompt('Reason for rejection (sent to NGO):')
                            if (reason === null) return
                            try {
                              setLoading(true)
                              await supabase.from('nfp_organisations').update({
                                application_status: 'rejected',
                                admin_notes: reason,
                                reviewed_by: (await supabase.auth.getUser()).data.user?.id,
                                reviewed_at: new Date().toISOString()
                              }).eq('id', app.id)
                              await load()
                            } catch (e: any) { alert(e.message) } finally { setLoading(false) }
                          }}
                          className="w-full py-3 bg-white text-slate-400 border border-slate-200 font-bold rounded-xl hover:bg-red-50 hover:text-red-500 hover:border-red-100 transition-all"
                        >
                          Decline
                        </button>
                      </div>
                    )}
                    {app.application_status === 'approved' && (
                      <div className="bg-emerald-50 text-emerald-700 px-4 py-3 rounded-2xl border border-emerald-100 flex items-center gap-3">
                        <CheckCircle2 size={20} className="flex-shrink-0" />
                        <div>
                          <p className="font-black text-[10px] uppercase tracking-widest">Active Partner</p>
                          <p className="text-xs font-bold opacity-80 mt-0.5">Approved & Provisioned</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {nfpApps.length === 0 && (
                <div className="p-20 text-center space-y-4">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto text-slate-300">
                    <AlertCircle size={32} />
                  </div>
                  <div className="max-w-xs mx-auto">
                    <p className="font-black text-slate-800 text-lg">No Applications</p>
                    <p className="text-slate-400 font-medium">When NFPs apply via the partner portal, they will appear here for review.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  )
}